import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, type TableColumn } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { ConfirmationModal } from '../../components/ui/ConfirmationModal';
import { Dropdown, type DropdownItem } from '../../components/ui/Dropdown';
import {
  subscriptionPlanManagementService,
  type SubscriptionPlanWithCount,
  type CreatePlanData,
  type UpdatePlanData,
} from '../../services/admin';

/**
 * Plan Form Component
 * Separated to prevent re-creation on each render (which causes input focus loss)
 */
interface PlanFormProps {
  formData: CreatePlanData;
  newFeature: string;
  onUpdateFormField: (field: keyof CreatePlanData, value: any) => void;
  onUpdateLimitField: (field: keyof CreatePlanData['limits'], value: number) => void;
  onNewFeatureChange: (value: string) => void;
  onAddFeature: () => void;
  onRemoveFeature: (index: number) => void;
  t: (key: string) => string;
}

const PlanForm: React.FC<PlanFormProps> = ({
  formData,
  newFeature,
  onUpdateFormField,
  onUpdateLimitField,
  onNewFeatureChange,
  onAddFeature,
  onRemoveFeature,
  t,
}) => (
  <div className="space-y-4">
    {/* Basic Info */}
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscription_plan_management.form.name_label')} *</label>
        <Input
          value={formData.name}
          onChange={(e) => onUpdateFormField('name', e.target.value)}
          placeholder={t('subscription_plan_management.form.name_placeholder')}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscription_plan_management.form.slug_label')}</label>
        <Input
          value={formData.slug || ''}
          onChange={(e) => onUpdateFormField('slug', e.target.value)}
          placeholder={t('subscription_plan_management.form.slug_placeholder')}
        />
        <p className="mt-1 text-xs text-gray-500">{t('subscription_plan_management.form.slug_help')}</p>
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscription_plan_management.form.description_label')}</label>
      <textarea
        value={formData.description}
        onChange={(e) => onUpdateFormField('description', e.target.value)}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        rows={2}
        placeholder={t('subscription_plan_management.form.description_placeholder')}
      />
    </div>

    {/* Pricing */}
    <div className="grid grid-cols-3 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscription_plan_management.form.price_label')} *</label>
        <Input
          type="number"
          value={formData.price}
          onChange={(e) => onUpdateFormField('price', Number(e.target.value))}
          step="0.01"
          min="0"
          placeholder={t('subscription_plan_management.form.price_placeholder')}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscription_plan_management.form.billing_period_label')} *</label>
        <select
          value={formData.billing_period}
          onChange={(e) => onUpdateFormField('billing_period', e.target.value as 'monthly' | 'yearly')}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="monthly">{t('subscription_plan_management.billing_periods.monthly')}</option>
          <option value="yearly">{t('subscription_plan_management.billing_periods.yearly')}</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscription_plan_management.form.sort_order_label')}</label>
        <Input
          type="number"
          value={formData.sort_order}
          onChange={(e) => onUpdateFormField('sort_order', Number(e.target.value))}
          min="0"
          placeholder={t('subscription_plan_management.form.sort_order_placeholder')}
        />
        <p className="mt-1 text-xs text-gray-500">{t('subscription_plan_management.form.sort_order_help')}</p>
      </div>
    </div>

    {/* Limits */}
    <div>
      <h3 className="text-sm font-medium text-gray-900 mb-2">{t('subscription_plan_management.form.tab_limits')}</h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscription_plan_management.form.max_campaigns_label')} *</label>
          <Input
            type="number"
            value={formData.limits.max_campaigns}
            onChange={(e) => onUpdateLimitField('max_campaigns', Number(e.target.value))}
            min="-1"
            placeholder={t('subscription_plan_management.form.max_campaigns_placeholder')}
          />
          <p className="mt-1 text-xs text-gray-500">{t('subscription_plan_management.form.max_campaigns_help')}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Domains per Campaign *</label>
          <Input
            type="number"
            value={formData.domains_per_campaign}
            onChange={(e) => onUpdateFormField('domains_per_campaign', Number(e.target.value))}
            min="1"
            placeholder="e.g., 3"
          />
          <p className="mt-1 text-xs text-gray-500">Maximum number of domains per campaign. Use -1 for unlimited.</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscription_plan_management.form.max_team_members_label')} *</label>
          <Input
            type="number"
            value={formData.limits.max_team_members}
            onChange={(e) => onUpdateLimitField('max_team_members', Number(e.target.value))}
            min="-1"
            placeholder={t('subscription_plan_management.form.max_team_members_placeholder')}
          />
          <p className="mt-1 text-xs text-gray-500">{t('subscription_plan_management.form.max_team_members_help')}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscription_plan_management.form.max_requests_per_month_label')} *</label>
          <Input
            type="number"
            value={formData.limits.max_requests_per_month}
            onChange={(e) => onUpdateLimitField('max_requests_per_month', Number(e.target.value))}
            min="-1"
            placeholder={t('subscription_plan_management.form.max_requests_per_month_placeholder')}
          />
          <p className="mt-1 text-xs text-gray-500">{t('subscription_plan_management.form.max_requests_per_month_help')}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('subscription_plan_management.form.data_retention_days_label')} *</label>
          <Input
            type="number"
            value={formData.limits.data_retention_days}
            onChange={(e) => onUpdateLimitField('data_retention_days', Number(e.target.value))}
            min="1"
            placeholder={t('subscription_plan_management.form.data_retention_days_placeholder')}
          />
          <p className="mt-1 text-xs text-gray-500">{t('subscription_plan_management.form.data_retention_days_help')}</p>
        </div>
      </div>
    </div>

    {/* Features */}
    <div>
      <h3 className="text-sm font-medium text-gray-900 mb-2">{t('subscription_plan_management.form.features_label')}</h3>
      <div className="space-y-2">
        {/* List of features */}
        {formData.features && formData.features.length > 0 && (
          <ul className="space-y-2">
            {formData.features.map((feature, index) => (
              <li key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md">
                <span className="text-sm text-gray-700">{feature}</span>
                <button
                  type="button"
                  onClick={() => onRemoveFeature(index)}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  {t('subscription_plan_management.form.remove_feature_button')}
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Add new feature */}
        <div className="flex gap-2">
          <Input
            value={newFeature}
            onChange={(e) => onNewFeatureChange(e.target.value)}
            placeholder={t('subscription_plan_management.form.features_placeholder')}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onAddFeature();
              }
            }}
          />
          <Button
            type="button"
            variant="secondary"
            onClick={onAddFeature}
            className="whitespace-nowrap"
          >
            {t('subscription_plan_management.form.add_feature_button')}
          </Button>
        </div>
      </div>
    </div>

    {/* Active Status */}
    <div className="flex items-center">
      <input
        type="checkbox"
        id="is_active"
        checked={formData.is_active}
        onChange={(e) => onUpdateFormField('is_active', e.target.checked)}
        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
        {t('subscription_plan_management.form.is_active_label')}
      </label>
    </div>
  </div>
);

/**
 * Subscription Plan Management Page (Admin Only)
 *
 * Allows system administrators to:
 * - View all subscription plans
 * - Create new plans
 * - Edit existing plans
 * - Toggle plan active/inactive status
 * - Delete plans (if no accounts use them)
 */
export const SubscriptionPlanManagementPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [plans, setPlans] = useState<SubscriptionPlanWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal states
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState<{ open: boolean; plan: SubscriptionPlanWithCount | null }>({
    open: false,
    plan: null,
  });
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; planId: number | null; planName: string | null }>({
    open: false,
    planId: null,
    planName: null,
  });

  // Form data
  const [formData, setFormData] = useState<CreatePlanData>({
    name: '',
    description: '',
    price: 0,
    billing_period: 'monthly',
    is_active: true,
    sort_order: 0,
    features: [],
    domains_per_campaign: 1,
    limits: {
      max_campaigns: 0,
      max_team_members: 0,
      max_requests_per_month: 0,
      data_retention_days: 7,
    },
  });

  // Memoized update functions to prevent input focus loss
  const updateFormField = React.useCallback((field: keyof CreatePlanData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const updateLimitField = React.useCallback((field: keyof CreatePlanData['limits'], value: number) => {
    setFormData(prev => ({ ...prev, limits: { ...prev.limits, [field]: value } }));
  }, []);

  // Feature management
  const [newFeature, setNewFeature] = useState('');

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({ ...prev, features: [...prev.features, newFeature.trim()] }));
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    setFormData(prev => ({ ...prev, features: prev.features.filter((_, i) => i !== index) }));
  };

  // Fetch plans
  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await subscriptionPlanManagementService.getAllPlans();
      setPlans(response.plans);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('subscription_plan_management.messages.load_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  // Handle create plan
  const handleCreate = () => {
    setFormData({
      name: '',
      description: '',
      price: 0,
      billing_period: 'monthly',
      is_active: true,
      sort_order: 0,
      features: [],
      domains_per_campaign: 1,
      limits: {
        max_campaigns: 0,
        max_team_members: 0,
        max_requests_per_month: 0,
        data_retention_days: 7,
      },
    });
    setNewFeature('');
    setCreateModal(true);
  };

  const handleSubmitCreate = async () => {
    try {
      await subscriptionPlanManagementService.createPlan(formData);
      setCreateModal(false);
      await fetchPlans();
      setSuccess(t('subscription_plan_management.messages.create_success'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('subscription_plan_management.messages.create_error'));
    }
  };

  // Handle edit plan
  const handleEdit = (plan: SubscriptionPlanWithCount) => {
    setFormData({
      name: plan.name,
      description: plan.description || '',
      price: Number(plan.price),
      billing_period: plan.billing_period,
      is_active: plan.is_active,
      sort_order: plan.sort_order || 0,
      features: plan.features || [],
      domains_per_campaign: plan.domains_per_campaign || 1,
      limits: {
        max_campaigns: plan.limits?.campaigns || plan.limits?.max_campaigns || 0,
        max_team_members: plan.limits?.team_members || plan.limits?.max_team_members || 0,
        max_requests_per_month: plan.limits?.requests_per_month || plan.limits?.max_requests_per_month || 0,
        data_retention_days: plan.limits?.data_retention_days || 7,
      },
    });
    setNewFeature('');
    setEditModal({ open: true, plan });
  };

  const handleSubmitEdit = async () => {
    if (!editModal.plan) return;

    try {
      const updateData: UpdatePlanData = { ...formData };
      await subscriptionPlanManagementService.updatePlan(editModal.plan.id, updateData);
      setEditModal({ open: false, plan: null });
      await fetchPlans();
      setSuccess(t('subscription_plan_management.messages.update_success'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('subscription_plan_management.messages.update_error'));
    }
  };

  // Handle toggle status
  const handleToggleStatus = async (plan: SubscriptionPlanWithCount) => {
    try {
      await subscriptionPlanManagementService.togglePlanStatus(plan.id);
      await fetchPlans();
      setSuccess(t('subscription_plan_management.messages.toggle_success'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('subscription_plan_management.messages.toggle_error'));
    }
  };

  // Handle delete
  const handleDelete = (plan: SubscriptionPlanWithCount) => {
    setDeleteConfirm({ open: true, planId: plan.id, planName: plan.name });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.planId) return;

    try {
      const result = await subscriptionPlanManagementService.deletePlan(deleteConfirm.planId);
      setDeleteConfirm({ open: false, planId: null, planName: null });
      await fetchPlans();
      setSuccess(t('subscription_plan_management.messages.delete_success'));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('subscription_plan_management.messages.delete_error'));
    }
  };

  // Table columns
  const columns: TableColumn<SubscriptionPlanWithCount>[] = [
    {
      key: 'name',
      header: t('subscription_plan_management.table.plan_name'),
    },
    {
      key: 'price',
      header: t('subscription_plan_management.table.price'),
      render: (value, row) => `$${Number(value).toFixed(2)}/${row.billing_period}`,
    },
    {
      key: 'limits',
      header: t('subscription_plan_management.table.limits'),
      render: (value, row) => {
        const limits = value as CreatePlanData['limits'];
        const formatLimit = (num: number) => (num === -1 ? t('subscription_plan_management.table.unlimited') : num);
        return (
          <div className="text-xs space-y-1">
            <div>{formatLimit(limits.max_campaigns)} {t('subscription_plan_management.table.campaigns')}</div>
            <div>{formatLimit(row.domains_per_campaign || 1)} domains/campaign</div>
            <div>{formatLimit(limits.max_team_members)} {t('subscription_plan_management.table.members')}</div>
            <div>{formatLimit(limits.max_requests_per_month)} {t('subscription_plan_management.table.requests_mo')}</div>
          </div>
        );
      },
    },
    {
      key: 'accounts_count',
      header: t('subscription_plan_management.table.accounts'),
      render: (value) => value || 0,
    },
    {
      key: 'is_active',
      header: t('subscription_plan_management.table.status'),
      render: (value) => (
        <Badge variant={value ? 'success' : 'error'}>
          {value ? t('subscription_plan_management.status.active') : t('subscription_plan_management.status.inactive')}
        </Badge>
      ),
    },
    {
      key: 'sort_order',
      header: t('subscription_plan_management.table.order'),
    },
  ];

  // Actions dropdown
  const renderActions = (plan: SubscriptionPlanWithCount) => {
    const items: DropdownItem[] = [
      {
        id: 'edit',
        label: t('subscription_plan_management.actions.edit'),
        onClick: () => handleEdit(plan),
      },
      {
        id: 'toggle',
        label: plan.is_active ? t('subscription_plan_management.actions.deactivate') : t('subscription_plan_management.actions.activate'),
        onClick: () => handleToggleStatus(plan),
      },
      {
        id: 'delete',
        label: t('subscription_plan_management.actions.delete'),
        onClick: () => handleDelete(plan),
        danger: true,
        divider: true,
      },
    ];

    return (
      <Dropdown
        trigger={
          <Button variant="secondary" size="sm">
            {t('subscription_plan_management.table.actions')}
          </Button>
        }
        items={items}
        align="right"
      />
    );
  };


  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">{t('subscription_plan_management.page_title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('subscription_plan_management.page_subtitle')}
          </p>
        </div>
        <Button onClick={handleCreate} className="w-full sm:w-auto">{t('subscription_plan_management.create_button')}</Button>
      </div>

      {/* Alerts */}
      {error && <Alert variant="error" onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Plans Table */}
      <Card>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <Table
            columns={columns}
            data={plans}
            renderActions={renderActions}
          />
        )}
      </Card>

      {/* Create Modal */}
      <Modal
        open={createModal}
        onClose={() => setCreateModal(false)}
        title={t('subscription_plan_management.modals.create_title')}
      >
        <PlanForm
          formData={formData}
          newFeature={newFeature}
          onUpdateFormField={updateFormField}
          onUpdateLimitField={updateLimitField}
          onNewFeatureChange={setNewFeature}
          onAddFeature={addFeature}
          onRemoveFeature={removeFeature}
          t={t}
        />
        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="secondary" onClick={() => setCreateModal(false)}>
            {t('subscription_plan_management.modals.cancel_button')}
          </Button>
          <Button onClick={handleSubmitCreate}>{t('subscription_plan_management.modals.create_confirm_button')}</Button>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={editModal.open}
        onClose={() => setEditModal({ open: false, plan: null })}
        title={t('subscription_plan_management.modals.edit_title')}
      >
        <PlanForm
          formData={formData}
          newFeature={newFeature}
          onUpdateFormField={updateFormField}
          onUpdateLimitField={updateLimitField}
          onNewFeatureChange={setNewFeature}
          onAddFeature={addFeature}
          onRemoveFeature={removeFeature}
          t={t}
        />
        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="secondary" onClick={() => setEditModal({ open: false, plan: null })}>
            {t('subscription_plan_management.modals.cancel_button')}
          </Button>
          <Button onClick={handleSubmitEdit}>{t('subscription_plan_management.modals.save_button')}</Button>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmationModal
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, planId: null, planName: null })}
        onConfirm={confirmDelete}
        title={t('subscription_plan_management.modals.delete_title')}
        message={t('subscription_plan_management.modals.delete_message')}
        description={deleteConfirm.planName || undefined}
        confirmText={t('subscription_plan_management.modals.delete_confirm_button')}
        variant="danger"
      />
    </div>
  );
};

SubscriptionPlanManagementPage.displayName = 'SubscriptionPlanManagementPage';
